import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Pause, Play } from 'lucide-react';
import { VideoPlayer, type PlayerSource } from '@mlm/ui';
import { cn } from '@mlm/ui/lib/cn';
import { controlSrc, detectPlaybackProvider } from '@mlm/shared';
import { apiErrorMessage, coaching, type FrequencyTrack } from '../lib/api';
import { qk } from '../lib/queries';
import { clockLabel } from '../lib/format';
import { CoachButton, ErrorNote } from './primitives';

/** Listening this long (or to the end) counts as the day's frequency practice. */
export const FREQUENCY_PRACTICE_SECONDS = 60;
/** Fetch a fresh URL this long before the current one expires. */
const URL_MARGIN_MS = 2 * 60 * 1000;
/** How far the immersive player's skip buttons jump. */
const SKIP_SECONDS = 15;

type StreamSource = {
  key: readonly unknown[];
  fetch: () => Promise<{ url: string; expiresAt: string }>;
};

type PlayerProps = {
  track: FrequencyTrack;
  onListened?: (seconds: number) => void;
  compact?: boolean;
  /** Where the playback URL comes from: the frequencies route by default; a program day passes its own. */
  source?: StreamSource;
  /**
   * `light` is the hairline player inside a page (a program day); `immersive`
   * is the redesign's full-screen player on the forest ground, with a seek bar
   * and skip buttons.
   */
  variant?: 'light' | 'immersive';
  /** Told when playback starts or stops, so the screen around it can animate. */
  onPlayingChange?: (playing: boolean) => void;
};

/**
 * One frequency track, uploaded or linked. For an upload the playback URL is
 * short-lived, so the player keeps the position and fetches a new one when it
 * expires or fails, then carries on from where the member was. Only time
 * actually PLAYED counts toward `onListened`: scrubbing to the end is not
 * listening.
 */
export function FrequencyPlayer(props: PlayerProps) {
  const embed = props.track.embed ?? null;
  // A linked track: a Vimeo (or YouTube) page plays on the shared player, which
  // drives the embed over its own protocol and reports time; a direct media
  // file plays on the ordinary audio element with no short-lived URL to fetch.
  if (embed?.kind === 'iframe') return <LinkedFrequencyPlayer {...props} src={embed.src} />;
  if (embed?.kind === 'video') {
    const url = embed.src;
    return (
      <StreamedFrequencyPlayer
        {...props}
        source={{
          key: ['frequency-link', props.track.id, url],
          fetch: async () => ({
            url,
            expiresAt: new Date(Date.now() + 365 * 86_400_000).toISOString(),
          }),
        }}
      />
    );
  }
  return <StreamedFrequencyPlayer {...props} />;
}

/** Real listening time only: playback advances in small steps, a seek is a jump. */
function useListenedCounter(onListened?: (seconds: number) => void) {
  const listened = useRef({ seconds: 0, last: 0, reported: false });
  const report = () => {
    if (listened.current.reported) return;
    listened.current.reported = true;
    onListened?.(Math.round(listened.current.seconds));
  };
  const advance = (t: number) => {
    const step = t - listened.current.last;
    if (step > 0 && step < 2) listened.current.seconds += step;
    listened.current.last = t;
    if (listened.current.seconds >= FREQUENCY_PRACTICE_SECONDS) report();
  };
  return { listened, report, advance };
}

/** Tells the screen around the player when playback starts or stops. */
function usePlayingSignal(playing: boolean, onPlayingChange?: (playing: boolean) => void) {
  const notify = useRef(onPlayingChange);
  notify.current = onPlayingChange;
  useEffect(() => {
    notify.current?.(playing);
  }, [playing]);
}

/**
 * A track that lives on Vimeo or YouTube: the shared course player drives the
 * embed and tells us the time, so a real minute of listening still counts.
 * The host's own privacy settings must allow this site, which is on the
 * tenant (a domain-locked video shows the host's refusal, not ours).
 */
function LinkedFrequencyPlayer({
  track,
  src,
  onListened,
  variant,
  onPlayingChange,
}: PlayerProps & { src: string }) {
  const { listened, report, advance } = useListenedCounter(onListened);
  const [playing, setPlaying] = useState(false);
  usePlayingSignal(playing, onPlayingChange);
  const source: PlayerSource = useMemo(() => {
    const provider = detectPlaybackProvider(src);
    return {
      kind: 'iframe',
      src: controlSrc(src, provider, { origin: window.location.origin, playsinline: true }),
      provider,
    };
  }, [src]);
  return (
    <div
      className={cn(
        variant === 'immersive'
          ? 'mt-[26px] overflow-hidden rounded-2xl border border-paper/10 bg-black/25'
          : 'py-2',
      )}
    >
      <VideoPlayer
        source={source}
        title={track.title}
        protocolId={track.id}
        chrome="embed-strip"
        preferences={{ rate: 1, captionsLang: null }}
        onEvent={(event) => {
          if (event.type === 'time') advance(event.currentTime);
          if (event.type === 'seeked') listened.current.last = event.currentTime;
          if (event.type === 'state') setPlaying(event.state === 'playing');
          if (event.type === 'state' && event.state === 'ended' && listened.current.seconds > 0) {
            report();
          }
        }}
      />
    </div>
  );
}

/**
 * The audio element and everything it needs: the short-lived URL and its
 * refresh, the position, the listening counter and the lock-screen name.
 * Both player looks render from this one hook.
 */
function useStreamedTrack({
  track,
  onListened,
  source,
}: {
  track: FrequencyTrack;
  onListened?: (seconds: number) => void;
  source?: StreamSource;
}) {
  const stream = useQuery({
    queryKey: source?.key ?? qk.frequencyStream(track.id),
    queryFn: source?.fetch ?? (() => coaching.frequencyStream(track.id)),
    staleTime: 50 * 60 * 1000,
    gcTime: 55 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
  const audio = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(track.durationSeconds ?? 0);
  const [failed, setFailed] = useState(false);
  const { listened, report, advance } = useListenedCounter(onListened);
  const resume = useRef<{ at: number; play: boolean } | null>(null);

  // The lock screen and notification shade name the track.
  useEffect(() => {
    if (!('mediaSession' in navigator) || typeof MediaMetadata === 'undefined') return;
    navigator.mediaSession.metadata = new MediaMetadata({
      title: track.title,
      artist: 'Miracles Protocol',
    });
  }, [track.title]);

  // Stop cleanly when the screen goes away: a playing <audio> that has left the
  // page keeps playing until something pauses it.
  const hasAudio = Boolean(stream.data);
  useEffect(() => {
    const el = audio.current;
    return () => el?.pause();
  }, [hasAudio]);

  const refreshUrl = async (play: boolean) => {
    resume.current = { at: audio.current?.currentTime ?? 0, play };
    await stream.refetch();
  };

  const toggle = async () => {
    const el = audio.current;
    if (!el) return;
    if (!el.paused) {
      el.pause();
      return;
    }
    const expiresAt = stream.data ? new Date(stream.data.expiresAt).getTime() : 0;
    if (expiresAt - Date.now() < URL_MARGIN_MS) {
      await refreshUrl(true);
      return;
    }
    await el.play().catch(() => undefined);
  };

  const seekTo = (seconds: number) => {
    const el = audio.current;
    if (!el || !duration) return;
    el.currentTime = Math.max(0, Math.min(duration, seconds));
    listened.current.last = el.currentTime;
    setPosition(el.currentTime);
  };

  const element = stream.data ? (
    <audio
      ref={audio}
      src={stream.data.url}
      preload="metadata"
      onLoadedMetadata={(e) => {
        const el = e.currentTarget;
        if (Number.isFinite(el.duration) && el.duration > 0) setDuration(el.duration);
        if (resume.current) {
          el.currentTime = resume.current.at;
          listened.current.last = resume.current.at;
          if (resume.current.play) void el.play().catch(() => undefined);
          resume.current = null;
        }
      }}
      onPlay={(e) => {
        listened.current.last = e.currentTarget.currentTime;
        setPlaying(true);
      }}
      onPause={() => setPlaying(false)}
      onTimeUpdate={(e) => {
        const t = e.currentTarget.currentTime;
        advance(t);
        setPosition(t);
      }}
      onEnded={() => {
        setPlaying(false);
        if (listened.current.seconds > 0) report();
      }}
      onError={() => {
        // Most often an expired URL: fetch a fresh one and pick up again,
        // once. A second failure is a track that cannot play.
        if (resume.current) {
          resume.current = null;
          setFailed(true);
          return;
        }
        void refreshUrl(playing);
      }}
    />
  ) : null;

  return {
    stream,
    element,
    playing,
    position,
    duration,
    failed,
    toggle,
    seek: (ratio: number) => seekTo(ratio * duration),
    skip: (seconds: number) => seekTo((audio.current?.currentTime ?? position) + seconds),
  };
}

const UNPLAYABLE = 'This track could not be played right now. Please try again later.';

function StreamedFrequencyPlayer({
  track,
  onListened,
  compact,
  source,
  variant = 'light',
  onPlayingChange,
}: PlayerProps) {
  const t = useStreamedTrack({ track, onListened, source });
  usePlayingSignal(t.playing, onPlayingChange);
  const pct = t.duration > 0 ? Math.min(100, (t.position / t.duration) * 100) : 0;
  const seekFromClick = (e: MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    t.seek((e.clientX - rect.left) / rect.width);
  };

  if (variant === 'immersive') {
    if (t.stream.isError || t.failed) {
      return (
        <div className="mt-[26px] rounded-2xl border border-paper/10 bg-paper/[0.05] px-4 py-4 text-center">
          <p className="text-sm leading-relaxed text-paper/80">
            {t.failed ? UNPLAYABLE : apiErrorMessage(t.stream.error)}
          </p>
          {!t.failed && (
            <button
              type="button"
              onClick={() => void t.stream.refetch()}
              className="mt-3 font-mono text-[0.64rem] uppercase tracking-[0.14em] text-[#E6C87A] underline underline-offset-4"
            >
              Try again
            </button>
          )}
        </div>
      );
    }
    const skipButton =
      'flex h-[54px] w-[54px] items-center justify-center rounded-full border border-paper/[0.16] bg-paper/[0.06] font-mono text-xs text-paper transition-colors hover:border-paper/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E6C87A] disabled:opacity-40';
    return (
      <>
        {t.element}
        <div className="mt-[26px]">
          <button
            type="button"
            aria-label="Seek"
            disabled={!t.duration}
            onClick={seekFromClick}
            className="block w-full py-2.5 disabled:cursor-default"
          >
            <span className="relative block h-1 rounded bg-paper/[0.14]">
              <span
                className="absolute inset-y-0 left-0 rounded bg-gradient-to-r from-verde to-[#E6C87A]"
                style={{ width: `${pct}%` }}
              />
              <span
                className="absolute top-1/2 -ml-[7px] -mt-[7px] h-3.5 w-3.5 rounded-full bg-paper shadow-[0_0_0_4px_rgba(246,244,238,0.15)]"
                style={{ left: `${pct}%` }}
              />
            </span>
          </button>
          <div className="flex justify-between font-mono text-xs tabular-nums text-paper/60">
            <span>{clockLabel(t.position)}</span>
            <span>{t.duration > 0 ? clockLabel(t.duration) : '--:--'}</span>
          </div>
        </div>
        <div className="mt-[18px] flex items-center justify-center gap-[26px]">
          <button
            type="button"
            aria-label={`Back ${SKIP_SECONDS} seconds`}
            disabled={!t.duration}
            onClick={() => t.skip(-SKIP_SECONDS)}
            className={skipButton}
          >
            −{SKIP_SECONDS}
          </button>
          <button
            type="button"
            aria-label={t.playing ? 'Pause' : 'Play'}
            disabled={!t.stream.data}
            onClick={() => void t.toggle()}
            className="flex h-20 w-20 items-center justify-center rounded-full bg-gold text-ink shadow-[0_14px_34px_-10px_rgba(176,141,63,0.7)] transition-colors hover:bg-[#c29d4c] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E6C87A] disabled:opacity-60"
          >
            {t.playing ? (
              <Pause className="h-7 w-7" fill="currentColor" aria-hidden />
            ) : (
              <Play className="ml-1 h-7 w-7" fill="currentColor" aria-hidden />
            )}
          </button>
          <button
            type="button"
            aria-label={`Forward ${SKIP_SECONDS} seconds`}
            disabled={!t.duration}
            onClick={() => t.skip(SKIP_SECONDS)}
            className={skipButton}
          >
            +{SKIP_SECONDS}
          </button>
        </div>
      </>
    );
  }

  if (t.stream.isError) {
    return <ErrorNote error={t.stream.error} retry={() => void t.stream.refetch()} />;
  }
  if (t.failed) {
    return <p className="text-sm leading-relaxed text-destructive">{UNPLAYABLE}</p>;
  }

  return (
    <div className={cn('space-y-4', !compact && 'py-2')}>
      {t.element}
      <div className="flex items-center gap-4">
        <CoachButton
          kind={compact ? 'outline' : 'gold'}
          aria-label={t.playing ? 'Pause' : 'Play'}
          disabled={!t.stream.data}
          onClick={() => void t.toggle()}
          className="min-w-[7.5rem]"
        >
          {t.playing ? (
            <Pause className="h-4 w-4" aria-hidden />
          ) : (
            <Play className="h-4 w-4" aria-hidden />
          )}
          {t.playing ? 'Pause' : t.stream.isPending ? 'Loading' : 'Play'}
        </CoachButton>
        <span className="font-mono text-sm tabular-nums text-sage">
          {clockLabel(t.position)}
          {t.duration > 0 && ` / ${clockLabel(t.duration)}`}
        </span>
      </div>
      <button
        type="button"
        aria-label="Seek"
        disabled={!t.duration}
        className="block w-full py-3 disabled:cursor-default"
        onClick={seekFromClick}
      >
        <span className="block h-px w-full bg-[rgba(16,32,26,0.14)]">
          <span className="block h-px bg-verde" style={{ width: `${pct}%` }} />
        </span>
      </button>
    </div>
  );
}
